import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAcceptPostMutation } from '../api/use-accept-post-mutation';
import { useRejectPostMutation } from '../api/use-reject-post-mutation';
import {
  Box,
  Typography,
  Avatar,
  Button,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import FlagIcon from '@mui/icons-material/Flag';
import PageContainer from './PageContainer';
import adhdVideo from '../assets/adhd.mp4';
import { formatDate, formatModerationDateTime, MOMENT_FORMATS } from '../utils/dateFormat';

const PostImage = styled('img')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: 8,
  display: 'block',
});

const PostVideo = styled('video')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: 8,
  display: 'block',
});

const ActionButton = styled(Button)(({ theme, variant }) => ({
  minWidth: 120,
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  fontSize: '14px',
  fontWeight: 600,
  textTransform: 'none',
  color: 'white',
  backgroundColor: variant === 'approve' ? theme.palette.success.main : theme.palette.error.main,
  '&:hover': {
    backgroundColor: variant === 'approve' ? theme.palette.success.dark : theme.palette.error.dark,
  },
}));

const DetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: theme.spacing(2, 0),
}));

const PostDetails = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const { posts, loadPosts } = useAuth();

  // Find the post data from the queue
  const queuePost = posts.find((post) => post.id === postId);

  // Format date and time as "25 May 2025 5:30pm"
  const formatDateTime = (dateString) => formatModerationDateTime(dateString) || '';

  // Dialog states
  const [isApprovalDialogOpen, setIsApprovalDialogOpen] = useState(false);
  const [approvalStep, setApprovalStep] = useState(1);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [rejectionStep, setRejectionStep] = useState(1);
  const [rejectionReason, setRejectionReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const acceptPostMutation = useAcceptPostMutation();
  const rejectPostMutation = useRejectPostMutation();

  // Constants
  const REJECTION_REASONS = [
    'Content violates community guidelines',
    'Inappropriate language or behavior',
    'Spam or promotional content',
    'Copyright infringement',
  ];

  // Generate post data from queue response
  const generatePostData = () => {
    if (!queuePost || !queuePost.originalData) {
      return null;
    }
    const originalData = queuePost.originalData;
    const content = originalData.content;
    const flaggedUser = originalData.flaggedUser;
    const isVideo = content.mediaType === 'video';

    return {
      id: postId,
      user: {
        name: flaggedUser.fullName || 'Unknown User',
        avatar: flaggedUser.avatarUrl || 'https://via.placeholder.com/40',
        followers: flaggedUser.followersCount || 0,
        following: flaggedUser.followingCount || 0,
      },
      type:
        originalData.violationTypes
          ?.map((type) => type.charAt(0).toUpperCase() + type.slice(1))
          .join(', ') || 'Unknown',
      dateTime: originalData.violationDateTime
        ? formatDateTime(originalData.violationDateTime)
        : 'N/A',
      caption: content.caption || 'No caption available',
      image: isVideo ? content.videoUrl || adhdVideo : content.imageUrl,
      isVideo: isVideo,
      comments: generateCommentsFromQueue(),
      violationDescription: originalData.violationDescription || 'Content under review',
      reportedBy: queuePost.reportedBy, // Use the processed value from AuthContext
    };
  };

  // Generate comments from queue data
  const generateCommentsFromQueue = () => {
    if (!queuePost || !queuePost.originalData.comments) {
      return [];
    }

    return queuePost.originalData.comments.map((comment) => ({
      id: comment.id,
      user: comment.author.fullName || 'Unknown User',
      text: comment.text,
      date: formatDate(comment.created_at, MOMENT_FORMATS.DATE) || '',
      type: comment.violationTypes && comment.violationTypes.length > 0 ? 'violation' : 'positive',
      violationType: comment.violationDescription || 'Content violation',
      avatar: comment.author.avatarUrl,
    }));
  };

  // Check for violations based on queue data
  const hasViolations = queuePost && queuePost.originalData.violationTypes?.length > 0;
  const hasAnyViolation = hasViolations;

  const postData = generatePostData();

  // If post is not found (e.g., after rejection), redirect to posts list
  if (!postData) {
    navigate('/posts');
    return null;
  }

  const handleApprove = () => {
    setIsApprovalDialogOpen(true);
    setApprovalStep(1);
  };

  const handleReject = () => {
    setIsRejectDialogOpen(true);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleApprovalConfirm = async () => {
    try {
      await acceptPostMutation.mutateAsync(postId);
      setApprovalStep(2);
      // Reload the moderation queue
      loadPosts();
    } catch (error) {
      console.error('Error accepting post:', error);
    }
  };

  const handleApprovalFinal = () => {
    setIsApprovalDialogOpen(false);
    setApprovalStep(1);
    navigate('/posts');
  };

  const handleRejectConfirm = async () => {
    try {
      const reason = rejectionReason === 'Other' ? otherReason : rejectionReason;
      await rejectPostMutation.mutateAsync({ postId, reason });
      setRejectionStep(2);
      // Reload the moderation queue
      loadPosts();
    } catch (error) {
      console.error('Error rejecting post:', error);
    }
  };

  const handleRejectFinal = () => {
    setIsRejectDialogOpen(false);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
    navigate('/posts');
  };

  const handleDialogClose = () => {
    setIsApprovalDialogOpen(false);
    setIsRejectDialogOpen(false);
    setApprovalStep(1);
    setRejectionStep(1);
    setRejectionReason('');
    setOtherReason('');
  };

  const handleReasonChange = (event) => {
    setRejectionReason(event.target.value);
    if (event.target.value !== 'Other') {
      setOtherReason('');
    }
  };

  const handleOtherReasonChange = (event) => {
    setOtherReason(event.target.value);
  };

  return (
    <PageContainer title="Post Details">
      <Grid container spacing={4}>
        {/* Left Column - Post Details */}
        <Grid item xs={12} md={6} sx={{ borderRight: { md: '1px solid #e0e0e0' }, pr: { md: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Avatar src={postData.user.avatar} sx={{ mr: 2, width: 56, height: 56 }}>
                {postData.user.name.charAt(0)}
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                  {postData.user.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {postData.user.followers} Followers • {postData.user.following} Following
                </Typography>
              </Box>
            </Box>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Type
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {postData.type}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Date & Time
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {postData.dateTime}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Caption
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
              >
                {postData.caption}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Reported By
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {postData.reportedBy}
              </Typography>
            </DetailRow>

            <DetailRow>
              <Typography variant="body1" color="text.secondary">
                Violation Description
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}
              >
                {postData.violationDescription}
              </Typography>
            </DetailRow>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, mt: 3, width: '100%' }}>
            <ActionButton
              variant="reject"
              startIcon={<CloseIcon />}
              onClick={handleReject}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Reject
            </ActionButton>
            <ActionButton
              variant="approve"
              startIcon={<CheckIcon />}
              onClick={handleApprove}
              sx={{ flex: 1, minWidth: 0 }}
            >
              Approve
            </ActionButton>
          </Box>
        </Grid>

        {/* Right Column - Image and Comments */}
        <Grid item xs={12} md={6} sx={{ pl: { md: 3 } }}>
          <Box sx={{ mb: 3 }}>
            <Box>
              {postData.isVideo ? (
                <PostVideo controls loop={false}>
                  <source src={postData.image} type="video/mp4" />
                  Your browser does not support the video tag.
                </PostVideo>
              ) : (
                <PostImage src={postData.image} alt="Post content" />
              )}
            </Box>
            {hasAnyViolation && (
              <Box
                sx={{
                  mt: 1,
                  backgroundColor: '#ffebee',
                  border: '1px solid #f44336',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ fontWeight: 600, display: 'block' }}
                >
                  {(() => {
                    const violationTypes = queuePost?.originalData?.violationTypes || [];
                    if (violationTypes.includes('image')) return 'Offensive Image';
                    if (violationTypes.includes('video')) return 'Offensive Video';
                    if (violationTypes.includes('caption')) return 'Offensive Caption';
                    if (violationTypes.includes('comment')) return 'Offensive Comment';
                    return 'Content Violation';
                  })()}
                </Typography>
              </Box>
            )}
          </Box>

          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Comments
          </Typography>

          <Box sx={{ border: '1px solid rgba(255, 152, 0, 0.3)', borderRadius: 1, p: 2 }}>
            <List sx={{ p: 0 }}>
              {postData.comments.map((comment, index) => (
                <Box key={comment.id}>
                  <ListItem
                    sx={{
                      px: 0,
                      alignItems: 'flex-start',
                      py: 1,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ width: 32, height: 32 }} src={comment.avatar}>
                        {comment.user.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      sx={{ ml: 1 }}
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            {comment.user}
                          </Typography>
                          {comment.type === 'violation' && (
                            <FlagIcon color="error" fontSize="small" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.5,
                              ...(comment.type === 'violation' && {
                                backgroundColor: '#ffebee',
                                borderRadius: 1,
                                border: '1px solid #f44336',
                                p: 1,
                                display: 'inline-block',
                              }),
                            }}
                          >
                            {comment.text}
                          </Typography>
                          {comment.type !== 'violation' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 1, display: 'block' }}
                            >
                              {comment.date}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < postData.comments.length - 1 && (
                    <Box
                      sx={{
                        borderBottom: '1px solid',
                        borderBottomColor: 'divider',
                        mx: 0,
                        my: 0.5,
                      }}
                    />
                  )}
                </Box>
              ))}
            </List>
          </Box>
        </Grid>
      </Grid>

      {/* Approval Dialog - Step 1: Confirmation Question */}
      <Dialog
        open={isApprovalDialogOpen && approvalStep === 1}
        onClose={handleDialogClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '16px',
            textAlign: 'center',
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box
            sx={{
              p: 2,
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
            }}
          >
            <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>
            Are you sure you want to approve this post?
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
            {postId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 0, gap: 2 }}>
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              color: 'warning.main',
              borderColor: 'warning.main',
              '&:hover': {
                backgroundColor: 'warning.light',
                borderColor: 'warning.main',
              },
              minWidth: 120,
              height: 48,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApprovalConfirm}
            variant="contained"
            sx={{
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
              minWidth: 120,
              height: 48,
            }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog - Step 2: Success Confirmation */}
      <Dialog
        open={isApprovalDialogOpen && approvalStep === 2}
        onClose={handleDialogClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '16px',
            textAlign: 'center',
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box
            sx={{
              p: 2,
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'success.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto',
            }}
          >
            <CheckIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="body2" sx={{ mt: 3, mb: 1 }}>
            You have successfully approved the post
          </Typography>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {postId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 0, px: 3 }}>
          <Button
            onClick={handleApprovalFinal}
            variant="contained"
            sx={{
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 300 },
              height: 48,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog - Step 1: Reason Selection */}
      <Dialog
        open={isRejectDialogOpen && rejectionStep === 1}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'left', fontWeight: 'bold' }}>Reject Post</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              textAlign: 'left',
              mb: 2,
              mt: 1,
            }}
          >
            Before you proceed, please provide a reason for rejecting this post
          </Typography>
          <RadioGroup value={rejectionReason} onChange={handleReasonChange}>
            {REJECTION_REASONS.map((text, index) => (
              <FormControlLabel
                key={index}
                value={text}
                control={<Radio />}
                label={text}
                sx={{ mb: 2, borderBottom: '1px solid', borderBottomColor: 'divider', pb: 2 }}
              />
            ))}
            <FormControlLabel value="Other" control={<Radio />} label="Other" />
          </RadioGroup>
          {rejectionReason === 'Other' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={otherReason}
              onChange={handleOtherReasonChange}
              placeholder="Please specify the reason"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'center',
            gap: 1.5,
            px: 3,
            py: 3,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Button
            onClick={handleDialogClose}
            variant="outlined"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              height: 48,
              color: 'warning.main',
              borderColor: 'warning.main',
              '&:hover': {
                backgroundColor: 'warning.light',
                borderColor: 'warning.main',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            disabled={!rejectionReason || (rejectionReason === 'Other' && !otherReason)}
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              height: 48,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog - Step 2: Confirmation */}
      <Dialog
        open={isRejectDialogOpen && rejectionStep === 2}
        onClose={handleDialogClose}
        PaperProps={{
          style: {
            borderRadius: 16,
            padding: '24px',
            textAlign: 'center',
            maxWidth: '400px',
          },
        }}
      >
        <DialogContent sx={{ pb: 2 }}>
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              margin: '0 auto 24px',
            }}
          >
            <CloseIcon sx={{ fontSize: 40, color: 'white' }} />
          </Box>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You have rejected the post
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
            {postId}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pt: 0, px: 3 }}>
          <Button
            onClick={handleRejectFinal}
            variant="contained"
            sx={{
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: 200 },
              height: 48,
              backgroundColor: 'warning.main',
              color: 'white',
              '&:hover': {
                backgroundColor: 'warning.dark',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default PostDetails;
